import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import i18n from "@mirage/translations";
import { useMemo } from "react";
import { useQuerySuggestions } from "./useQuerySuggestions";

import type { SearchResult } from "@mirage/service-dbx-api";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";

const logger = tagged("useDocumentFollowUpSuggestions");

export type DocumentFollowUpSuggestion = {
  id: string;
  text: string;
  prompt: string;
  category: "analyze" | "summarize" | "brainstorm" | "extract" | "create";
};

const createStaticSuggestions = (
  _result: SearchResult,
): QuerySuggestionResult[] => {
  return [
    {
      uuid: "query_suggestions_summarize_key_points",
      text: i18n.t("query_suggestions_summarize_key_points"),
      isStaticSuggestion: true,
    },
    {
      uuid: "query_suggestions_analyze_insights",
      text: i18n.t("query_suggestions_analyze_insights"),
      isStaticSuggestion: true,
    },
    {
      uuid: "query_suggestions_brainstorm_questions",
      text: i18n.t("query_suggestions_brainstorm_questions"),
      isStaticSuggestion: true,
    },
    {
      uuid: "query_suggestions_find_related_docs",
      text: i18n.t("query_suggestions_find_related_docs"),
      isStaticSuggestion: true,
    },
    {
      uuid: "query_suggestions_create_action_items",
      text: i18n.t("query_suggestions_create_action_items"),
      isStaticSuggestion: true,
    },
  ];
};

const generatePromptForSuggestion = (
  suggestion: QuerySuggestionResult,
  title: string,
): string => {
  switch (suggestion.uuid) {
    case "query_suggestions_summarize_key_points":
      return `Please summarize the key points from "${title}"`;
    case "query_suggestions_analyze_insights":
      return `What are the main insights or takeaways from "${title}"?`;
    case "query_suggestions_brainstorm_questions":
      return `What questions could I ask to better understand "${title}"?`;
    case "query_suggestions_find_related_docs":
      return `What other documents are related to "${title}"?`;
    case "query_suggestions_create_action_items":
      return `What action items should I take based on "${title}"?`;
    default:
      return suggestion.text;
  }
};

const convertToFollowUpSuggestion = (
  suggestion: QuerySuggestionResult,
  index: number,
  title: string,
): DocumentFollowUpSuggestion => {
  const categories: Array<
    "analyze" | "summarize" | "brainstorm" | "extract" | "create"
  > = ["analyze", "summarize", "brainstorm", "extract", "create"];

  return {
    id: suggestion.uuid || `dynamic_${index}`,
    text: suggestion.text,
    prompt: generatePromptForSuggestion(suggestion, title),
    category: categories[index % categories.length],
  };
};

export function useDocumentFollowUpSuggestions(result: SearchResult) {
  const conversationsExperimentSetting = useFeatureFlagValue(
    "context_engine_2025_03_24_conversations_setting",
  );

  const staticSuggestions = useMemo(
    () => createStaticSuggestions(result),
    [result.title],
  );

  const queryOptions = useMemo(() => {
    if (!result.uuid) {
      return { source: "dash" as const };
    }

    return {
      source: "dash" as const,
      documentIds: [result.uuid],
      experimentSetting: String(conversationsExperimentSetting),
      maxSuggestions: 5,
    };
  }, [result.uuid, conversationsExperimentSetting]);

  logger.debug("Using working Stack pattern", {
    hasDocumentId: !!result.uuid,
    documentId: result.uuid,
    experimentSetting: String(conversationsExperimentSetting),
    queryOptions,
  });

  const { isLoading, querySuggestions } = useQuerySuggestions({
    maxSuggestions: 5,
    options: queryOptions,
    demoSuggestions: staticSuggestions,
  });

  logger.debug("useQuerySuggestions response", {
    isLoading,
    querySuggestionsLength: querySuggestions.length,
    querySuggestions: querySuggestions.map((s, i) => ({
      index: i,
      uuid: s.uuid,
      text: s.text?.slice(0, 50) + (s.text?.length > 50 ? "..." : ""),
      hasText: !!s.text,
    })),
  });

  const suggestions = useMemo(() => {
    logger.debug("Processing suggestions", {
      totalQuerySuggestions: querySuggestions.length,
      firstSuggestionText: querySuggestions[0]?.text || "none",
      isLoading,
      hasDocumentId: !!result.uuid,
    });

    const title = result.title || "this document";

    return querySuggestions
      .filter(
        (suggestion) => suggestion.text && suggestion.text.trim().length > 0,
      )
      .map((suggestion, index) =>
        convertToFollowUpSuggestion(suggestion, index, title),
      );
  }, [querySuggestions, isLoading, result.uuid, result.title]);

  return {
    suggestions,
    isLoading,
  };
}
